export type ThemeBasePalette = {
  background: string;
  panel: string;
  accent: string;
  text: string;
};

export type VisualMode = "terminal" | "modern";

export type ThemePresetId = "matrix" | "amber" | "ice" | "graphite" | "sand" | "concrete" | "basalt";

export type ThemePreference = {
  presetId: ThemePresetId | "custom";
  palette: ThemeBasePalette;
  visualMode: VisualMode;
  tactility: number;
  tokenOverrides?: Record<string, string>;
};

export type ThemePreset = {
  id: ThemePresetId;
  label: string;
  description: string;
  palette: ThemeBasePalette;
  visualMode: VisualMode;
  defaultTactility?: number;
  /** Applied after `deriveThemeTokens` when this preset is active */
  tokenOverrides?: Record<string, string>;
};

type Rgb = {
  r: number;
  g: number;
  b: number;
};

export const THEME_STORAGE_KEY = "styx_v1_theme_preference";

/** Per-account cache in localStorage (server is source of truth across devices). */
export function themePreferenceStorageKeyForUser(userId: string): string {
  return `${THEME_STORAGE_KEY}:${userId}`;
}

export const THEME_CHANGE_EVENT = "styx-theme-change";

/** sessionStorage: last user id that had theme saved under the per-user localStorage key (for pre-React bootstrap). */
export const THEME_LAST_USER_SESSION_KEY = "styx_v1_theme_last_user_id";

/** Modern UI: bundled Inter + Windows Segoe UI / system sans fallbacks */
export const FONT_STACK_UI_MODERN =
  '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';

export const FONT_STACK_UI_MANROPE =
  '"Manrope", "Segoe UI", system-ui, -apple-system, sans-serif';

export const FONT_STACK_UI_ONEST =
  '"Onest", "Segoe UI", system-ui, -apple-system, sans-serif';

export const FONT_STACK_UI_DM_SANS =
  '"DM Sans", "Segoe UI", system-ui, -apple-system, sans-serif';

/** System sans only (no bundled Inter) */
export const FONT_STACK_UI_SYSTEM_SANS = '"Segoe UI", system-ui, -apple-system, sans-serif';

/** Terminal UI — bundled mono fonts first */
export const FONT_STACK_UI_TERMINAL =
  '"IBM Plex Mono", "Ubuntu Mono", "Courier New", "Lucida Console", monospace';

/** UI with Ubuntu Mono first (lab / custom) */
export const FONT_STACK_UI_UBUNTU_FIRST =
  '"Ubuntu Mono", "IBM Plex Mono", "Courier New", "Lucida Console", monospace';

/** Monospace: IBM Plex + Ubuntu Mono + common system monospaces */
export const FONT_STACK_MONO =
  '"IBM Plex Mono", "Ubuntu Mono", "Courier New", Consolas, "Cascadia Mono", "Segoe UI Mono", "Lucida Console", ui-monospace, monospace';

export const FONT_STACK_MONO_UBUNTU_FIRST =
  '"Ubuntu Mono", "IBM Plex Mono", "Courier New", Consolas, "Segoe UI Mono", "Lucida Console", ui-monospace, monospace';

export const FONT_STACK_MONO_SYSTEM =
  'ui-monospace, "Cascadia Mono", "Segoe UI Mono", Consolas, "Ubuntu Mono", "IBM Plex Mono", "Courier New", monospace';

/** Accent font stack for labels, badges, timestamps — IBM Plex Mono first */
export const FONT_STACK_ACCENT =
  '"IBM Plex Mono", "Ubuntu Mono", "Courier New", monospace';

/** Fractal noise texture with caller-controlled opacity for tactile surface feel. */
export function buildNoiseTextureUri(opacity: number): string {
  const alphaValue = clampUnit(opacity).toFixed(3);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='${alphaValue}'/%3E%3C/svg%3E")`;
}

/**
 * Dark-adapted noise: range compressed to 0-15% luminance via feComponentTransfer
 * so it adds subtle grain on dark surfaces without washing out the background.
 * Finer grain (0.75 freq, 5 octaves) mimics micro-crystalline basalt structure.
 */
export function buildDarkNoiseTextureUri(opacity: number): string {
  const alphaValue = clampUnit(opacity).toFixed(3);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncR type='linear' slope='0.15' intercept='0'/%3E%3CfeFuncG type='linear' slope='0.15' intercept='0'/%3E%3CfeFuncB type='linear' slope='0.15' intercept='0'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='${alphaValue}'/%3E%3C/svg%3E")`;
}

export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    id: "matrix",
    label: "Matrix Night",
    description: "Классический неоновый терминал",
    visualMode: "terminal",
    palette: {
      background: "#000000",
      panel: "#060d06",
      accent: "#00ff41",
      text: "#a9d9a9",
    },
  },
  {
    id: "amber",
    label: "Amber Relay",
    description: "Тёплый CRT с янтарным свечением",
    visualMode: "terminal",
    palette: {
      background: "#080401",
      panel: "#140c03",
      accent: "#ffb347",
      text: "#ffd8a6",
    },
  },
  {
    id: "ice",
    label: "Ice Signal",
    description: "Холодный терминал с ледяным cyan-свечением",
    visualMode: "terminal",
    palette: {
      background: "#02070b",
      panel: "#0b1620",
      accent: "#6feaff",
      text: "#d8f6ff",
    },
  },
  {
    id: "graphite",
    label: "Graphite",
    description: "Спокойный современный тёмный стиль",
    visualMode: "modern",
    palette: {
      background: "#16171b",
      panel: "#1e2024",
      accent: "#7b8af5",
      text: "#b8bcc8",
    },
  },
  {
    id: "sand",
    label: "Sand",
    description: "Тёплые земляные тона без свечения",
    visualMode: "modern",
    palette: {
      background: "#1a1815",
      panel: "#24221e",
      accent: "#c9a67a",
      text: "#c8c0b4",
    },
  },
  {
    id: "concrete",
    label: "Concrete",
    description: "Светлый нейтральный серый интерфейс",
    visualMode: "modern",
    defaultTactility: 0.90,
    palette: {
      background: "#c7c7c7",
      panel: "#d9d9d9",
      accent: "#363636",
      text: "#000000",
    },
    tokenOverrides: {
      "--font-ui": FONT_STACK_UI_ONEST,
      "--font-mono": FONT_STACK_MONO,
      "--font-accent": FONT_STACK_ACCENT,
      "--link": "#4a6fa5",
      "--msg-own-bg": "#c7c7c7",
      "--msg-other-bg": "#d8d8d8",
      "--radius-sm": "4px",
      "--radius-md": "10px",
      "--radius-lg": "16px",
      "--radius-full": "9999px",
      "--ls-label": "0em",
      "--ls-ui": "0.01em",
      "--ls-brand": "0.01em",
    },
  },
  {
    id: "basalt",
    label: "Basalt",
    description: "Тёмный нейтральный ахроматический интерфейс",
    visualMode: "modern",
    defaultTactility: 0.90,
    palette: {
      background: "#161618",
      panel: "#1c1c1e",
      accent: "#a1a1a6",
      text: "#e5e5e7",
    },
    tokenOverrides: {
      "--font-ui": FONT_STACK_UI_ONEST,
      "--font-mono": FONT_STACK_MONO,
      "--font-accent": FONT_STACK_ACCENT,
      "--link": "#7ba3d4",
      "--msg-own-bg": "#2c2c2e",
      "--msg-other-bg": "#232326",
      "--radius-sm": "4px",
      "--radius-md": "10px",
      "--radius-lg": "16px",
      "--radius-full": "9999px",
      "--ls-label": "0em",
      "--ls-ui": "0.01em",
      "--ls-brand": "0.01em",
    },
  },
] as const;

export const DEFAULT_THEME_PRESET_ID: ThemePresetId = "concrete";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = buildThemePreferenceFromPreset(DEFAULT_THEME_PRESET_ID);

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function rgbToHex(rgb: Rgb): string {
  const toHex = (channel: number) => clampChannel(channel).toString(16).padStart(2, "0");
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

function rgbTriplet(rgb: Rgb): string {
  return `${clampChannel(rgb.r)}, ${clampChannel(rgb.g)}, ${clampChannel(rgb.b)}`;
}

function alpha(rgb: Rgb, opacity: number): string {
  return `rgba(${rgbTriplet(rgb)}, ${clampUnit(opacity)})`;
}

function mixRgb(from: Rgb, to: Rgb, amount: number): Rgb {
  const t = clampUnit(amount);
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  };
}

function parseHexColor(input: string): Rgb | null {
  const normalized = input.trim().toLowerCase();
  const short = normalized.match(/^#([\da-f]{3})$/i);
  if (short) {
    const [r, g, b] = short[1].split("");
    return {
      r: Number.parseInt(r + r, 16),
      g: Number.parseInt(g + g, 16),
      b: Number.parseInt(b + b, 16),
    };
  }
  const long = normalized.match(/^#([\da-f]{6})$/i);
  if (!long) return null;
  return {
    r: Number.parseInt(long[1].slice(0, 2), 16),
    g: Number.parseInt(long[1].slice(2, 4), 16),
    b: Number.parseInt(long[1].slice(4, 6), 16),
  };
}

function parseRgbColor(input: string): Rgb | null {
  const match = input.trim().match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+\s*)?\)$/i);
  if (!match) return null;
  return {
    r: clampChannel(Number(match[1])),
    g: clampChannel(Number(match[2])),
    b: clampChannel(Number(match[3])),
  };
}

function parseCssColor(input: string): Rgb | null {
  return parseHexColor(input) ?? parseRgbColor(input);
}

function normalizeHexColor(input: string, fallback: string): string {
  const parsed = parseCssColor(input) ?? parseCssColor(fallback);
  return parsed ? rgbToHex(parsed) : fallback;
}

function mapPaletteEntries(
  palette: ThemeBasePalette,
  fallback: ThemeBasePalette = DEFAULT_THEME_PREFERENCE.palette,
): ThemeBasePalette {
  return {
    background: normalizeHexColor(palette.background, fallback.background),
    panel: normalizeHexColor(palette.panel, fallback.panel),
    accent: normalizeHexColor(palette.accent, fallback.accent),
    text: normalizeHexColor(palette.text, fallback.text),
  };
}

function isPaletteShape(value: unknown): value is ThemeBasePalette {
  return typeof value === "object" && value !== null
    && "background" in value
    && "panel" in value
    && "accent" in value
    && "text" in value;
}

function sanitizeTokenOverrides(value: unknown): Record<string, string> | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const entries = Object.entries(value).filter(
    ([name, tokenValue]) => typeof name === "string" && typeof tokenValue === "string",
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function isThemePresetId(value: string): value is ThemePresetId {
  return THEME_PRESETS.some((preset) => preset.id === value);
}

export function isVisualMode(value: string): value is VisualMode {
  return value === "terminal" || value === "modern";
}

export function getThemePresetById(id: ThemePresetId): ThemePreset {
  return THEME_PRESETS.find((preset) => preset.id === id) ?? THEME_PRESETS[0];
}

export function buildThemePreferenceFromPreset(id: ThemePresetId): ThemePreference {
  const preset = getThemePresetById(id);
  return {
    presetId: preset.id,
    palette: { ...preset.palette },
    visualMode: preset.visualMode,
    tactility: preset.defaultTactility ?? (preset.visualMode === "modern" ? 0.18 : 0),
    tokenOverrides: preset.tokenOverrides ? { ...preset.tokenOverrides } : undefined,
  };
}

export function sanitizeThemePreference(value: unknown): ThemePreference {
  if (typeof value !== "object" || value === null) return DEFAULT_THEME_PREFERENCE;
  const maybePresetId = "presetId" in value && typeof value.presetId === "string" ? value.presetId : "custom";
  const maybePalette = "palette" in value && isPaletteShape(value.palette)
    ? value.palette
    : DEFAULT_THEME_PREFERENCE.palette;
  const maybeVisualMode = "visualMode" in value && typeof value.visualMode === "string" && isVisualMode(value.visualMode)
    ? value.visualMode
    : "terminal";
  const maybeTactility = "tactility" in value && typeof value.tactility === "number"
    ? clampUnit(value.tactility)
    : undefined;
  const maybeTokenOverrides = "tokenOverrides" in value
    ? sanitizeTokenOverrides(value.tokenOverrides)
    : undefined;
  const preset = isThemePresetId(maybePresetId) ? getThemePresetById(maybePresetId) : null;
  return {
    presetId: preset?.id ?? "custom",
    palette: mapPaletteEntries(preset ? preset.palette : maybePalette),
    visualMode: maybeVisualMode,
    tactility: maybeTactility ?? preset?.defaultTactility ?? (maybeVisualMode === "modern" ? 0.18 : 0),
    tokenOverrides: preset
      ? (preset.tokenOverrides ? { ...preset.tokenOverrides } : undefined)
      : maybeTokenOverrides,
  };
}

export function loadStoredThemePreference(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCE;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_THEME_PREFERENCE;
    return sanitizeThemePreference(JSON.parse(raw));
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function saveThemePreference(preference: ThemePreference): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preference));
}

/** When logged in, persist only the account key; when logged out, persist the legacy guest key. */
export function saveThemePreferenceForScope(accountUserId: string | null, preference: ThemePreference): void {
  if (typeof window === "undefined") return;
  const raw = JSON.stringify(preference);
  if (accountUserId) {
    window.localStorage.setItem(themePreferenceStorageKeyForUser(accountUserId), raw);
    try {
      window.sessionStorage.setItem(THEME_LAST_USER_SESSION_KEY, accountUserId);
    } catch {
      /* private mode */
    }
  } else {
    window.localStorage.setItem(THEME_STORAGE_KEY, raw);
    try {
      window.sessionStorage.removeItem(THEME_LAST_USER_SESSION_KEY);
    } catch {
      /* private mode */
    }
  }
}

/**
 * Load cached theme for an account (offline / first paint).
 * If the per-account key is missing, fall back to the legacy global key so we do not
 * reset to Concrete on refresh when the user had a theme only under `THEME_STORAGE_KEY`
 * (e.g. before per-account sync or if the first save ran while `accountUserId` was still null).
 */
export function loadStoredThemePreferenceForUser(userId: string): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCE;
  try {
    const rawUser = window.localStorage.getItem(themePreferenceStorageKeyForUser(userId));
    if (rawUser) return sanitizeThemePreference(JSON.parse(rawUser));
    const rawLegacy = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (rawLegacy) return sanitizeThemePreference(JSON.parse(rawLegacy));
    return DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

/** Prefer per-user cache when sessionStorage remembers the last logged-in user (aligns with `applyStoredThemePreference` / first React paint). */
export function loadStoredThemePreferenceForBootstrap(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCE;
  try {
    const uid = window.sessionStorage.getItem(THEME_LAST_USER_SESSION_KEY);
    if (uid) {
      return loadStoredThemePreferenceForUser(uid);
    }
  } catch {
    /* ignore */
  }
  return loadStoredThemePreference();
}

export function deriveThemeTokens(
  paletteInput: ThemeBasePalette,
  visualMode: VisualMode = "terminal",
  tactility = 0,
): Record<string, string> {
  const palette = mapPaletteEntries(paletteInput);
  const bg = parseHexColor(palette.background) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.background)!;
  const panel = parseHexColor(palette.panel) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.panel)!;
  const accent = parseHexColor(palette.accent) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.accent)!;
  const text = parseHexColor(palette.text) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.text)!;
  const black: Rgb = { r: 0, g: 0, b: 0 };
  const bgLuminance = (0.299 * bg.r + 0.587 * bg.g + 0.114 * bg.b) / 255;
  const isLight = bgLuminance > 0.45;

  const bgDeep = mixRgb(bg, black, isLight ? 0.08 : 0.22);
  const panelStrongBase = mixRgb(panel, bg, isLight ? 0.12 : 0.18);
  const panelSoftBase = mixRgb(panel, bg, isLight ? 0.05 : 0.08);
  const headerChrome = mixRgb(panel, bg, isLight ? 0.10 : 0.14);
  const greenDim = mixRgb(accent, bg, isLight ? 0.40 : 0.72);
  const greenMid = mixRgb(accent, panel, isLight ? 0.25 : 0.48);
  const greenFaint = mixRgb(accent, bg, isLight ? 0.60 : 0.9);
  const textDim = mixRgb(text, bg, isLight ? 0.28 : 0.48);
  const textMid = mixRgb(text, bg, isLight ? 0.08 : 0.2);
  const borderDim = mixRgb(accent, panel, isLight ? 0.55 : 0.8);
  const borderMid = mixRgb(accent, panel, isLight ? 0.38 : 0.62);

  const isModern = visualMode === "modern";
  const tactileStrength = isModern ? clampUnit(tactility) : 0;
  const textureOpacity = tactileStrength * 0.18;

  const glowValue = `0 0 8px ${alpha(accent, 0.4)}, 0 0 20px ${alpha(accent, 0.12)}`;
  const glowStrongValue = `0 0 12px ${alpha(accent, 0.58)}, 0 0 30px ${alpha(accent, 0.22)}`;

  // When adding new UI controls, prefer reusing existing tokens like --bg-deep / --panel-rgb.
  // Do not invent new *-rgb variables unless they are explicitly derived and documented here.
  return {
    "--bg": rgbToHex(bg),
    "--bg-rgb": rgbTriplet(bg),
    "--bg-deep": rgbToHex(bgDeep),
    "--panel": rgbToHex(panel),
    "--panel-rgb": rgbTriplet(panel),
    "--panel-strong": alpha(panelStrongBase, isLight ? 0.96 : 0.92),
    "--panel-soft": alpha(panelSoftBase, isLight ? 0.85 : 0.76),
    "--surface-soft": alpha(accent, isLight ? 0.06 : 0.035),
    "--surface-mid": alpha(accent, isLight ? 0.10 : 0.065),
    "--surface-strong": alpha(accent, isLight ? 0.16 : 0.12),
    "--surface-border": alpha(accent, isLight ? 0.24 : 0.18),
    "--header-chrome": alpha(headerChrome, isLight ? 0.96 : 0.92),
    "--accent": rgbToHex(accent),
    "--accent-rgb": rgbTriplet(accent),
    "--accent-02": alpha(accent, 0.02),
    "--accent-03": alpha(accent, 0.03),
    "--accent-04": alpha(accent, 0.04),
    "--accent-05": alpha(accent, 0.05),
    "--accent-06": alpha(accent, 0.06),
    "--accent-08": alpha(accent, 0.08),
    "--accent-10": alpha(accent, 0.1),
    "--accent-12": alpha(accent, 0.12),
    "--accent-18": alpha(accent, 0.18),
    "--accent-25": alpha(accent, 0.25),
    "--accent-45": alpha(accent, 0.45),
    "--green": rgbToHex(accent),
    "--green-dim": rgbToHex(greenDim),
    "--green-mid": rgbToHex(greenMid),
    "--green-faint": rgbToHex(greenFaint),
    "--text": rgbToHex(textMid),
    "--text-dim": rgbToHex(textDim),
    "--text-mid": rgbToHex(textMid),
    "--text-bright": rgbToHex(accent),
    "--border-dim": rgbToHex(borderDim),
    "--border-mid": rgbToHex(borderMid),
    "--glow": isModern ? "none" : glowValue,
    "--glow-strong": isModern ? "none" : glowStrongValue,
    "--err": "#cc4444",
    "--danger": "#cc4444",
    "--fg-muted": rgbToHex(textDim),
    "--link": isLight ? "#4a6fa5" : rgbToHex(accent),
    "--msg-own-bg": isLight ? "#c7c7c7" : alpha(accent, 0.065),
    "--msg-other-bg": isLight ? "#d8d8d8" : alpha(accent, 0.035),
    "--avatar-bg": isLight ? alpha(panel, 0.6) : alpha(accent, 0.06),
    "--texture": textureOpacity > 0.002
      ? (isLight ? buildNoiseTextureUri(textureOpacity) : buildDarkNoiseTextureUri(textureOpacity))
      : "none",
    "--font-ui": isModern ? FONT_STACK_UI_MODERN : FONT_STACK_UI_TERMINAL,
    "--font-mono": FONT_STACK_MONO,
    "--font-accent": isModern ? FONT_STACK_UI_MODERN : FONT_STACK_UI_TERMINAL,
    "--radius-sm": isModern ? "6px" : "0",
    "--radius-md": isModern ? "10px" : "0",
    "--radius-lg": isModern ? "16px" : "0",
    "--radius-full": isModern ? "9999px" : "0",
    "--shadow-elevated": isModern ? "0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.12)" : glowValue,
    "--ls-label": isModern ? "0.01em" : "0.08em",
    "--ls-ui": isModern ? "0.01em" : "0.06em",
    "--ls-brand": isModern ? "0.02em" : "5px",
  };
}

export function applyThemePreference(preference: ThemePreference, target?: HTMLElement): ThemePreference {
  const normalized = sanitizeThemePreference(preference);
  if (typeof document === "undefined") return normalized;
  const host = target ?? document.documentElement;
  const tokens = deriveThemeTokens(normalized.palette, normalized.visualMode, normalized.tactility);
  for (const [name, value] of Object.entries(tokens)) {
    host.style.setProperty(name, value);
  }
  if (normalized.tokenOverrides) {
    for (const [name, value] of Object.entries(normalized.tokenOverrides)) {
      host.style.setProperty(name, value);
    }
  }
  host.setAttribute("data-visual-mode", normalized.visualMode);
  if (!target && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: normalized }));
  }
  return normalized;
}

export function applyStoredThemePreference(): ThemePreference {
  const preference = loadStoredThemePreferenceForBootstrap();
  return applyThemePreference(preference);
}

export function readComputedThemePalette(target?: HTMLElement): ThemeBasePalette {
  const fallback = DEFAULT_THEME_PREFERENCE.palette;
  if (typeof document === "undefined") return fallback;
  const node = target ?? document.documentElement;
  const styles = getComputedStyle(node);
  return {
    background: normalizeHexColor(styles.getPropertyValue("--bg"), fallback.background),
    panel: normalizeHexColor(styles.getPropertyValue("--panel"), fallback.panel),
    accent: normalizeHexColor(styles.getPropertyValue("--accent") || styles.getPropertyValue("--green"), fallback.accent),
    text: normalizeHexColor(styles.getPropertyValue("--text-mid") || styles.getPropertyValue("--text"), fallback.text),
  };
}

export function getCanvasThemeColors(target?: HTMLElement): { backgroundFade: string; accentRgb: string } {
  const palette = readComputedThemePalette(target);
  const bg = parseHexColor(palette.background) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.background)!;
  const accent = parseHexColor(palette.accent) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.accent)!;
  return {
    backgroundFade: alpha(bg, 0.06),
    accentRgb: rgbTriplet(accent),
  };
}

export function getAvatarDitherLevels(target?: HTMLElement): readonly [number, number, number][] {
  const palette = readComputedThemePalette(target);
  const bg = parseHexColor(palette.background) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.background)!;
  const accent = parseHexColor(palette.accent) ?? parseHexColor(DEFAULT_THEME_PREFERENCE.palette.accent)!;
  return [
    [bg.r, bg.g, bg.b],
    Object.values(mixRgb(accent, bg, 0.72)).map(clampChannel) as [number, number, number],
    Object.values(mixRgb(accent, bg, 0.38)).map(clampChannel) as [number, number, number],
    [accent.r, accent.g, accent.b],
  ];
}
